import { useMemo, useState } from 'react';
import { isFunction } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { useDefaultId } from 'app/lib/hooks';
import { ConfigureServiceAccountPanel } from 'app/components/features/JobDialog/ConfigureServiceAccountPanel.jsx';
import { ConfigureEnvironmentPanel } from 'app/components/features/JobDialog/ConfigureEnvironmentPanel.jsx';
import {
    DialogWithChapters,
    DialogContext,
    DialogSummary,
    DialogConfigurePanel,
    DialogSidebar,
    DialogFormContent
} from 'app/components/features/DialogWithChapters';
import { CreateProjectForm } from './CreateProjectForm.jsx';

export function CreateProjectWithModelDialog({ onClose }) {
    const { t } = useTranslation();
    const accId = useDefaultId('panel-acc');
    const envId = useDefaultId('panel-env');
    const projectId = useDefaultId('panel-project');

    const [accSummary, setAccSummary] = useState(null);
    const [envSummary, setEnvSummary] = useState(null);
    const [projectSummary, setProjectSummary] = useState({});

    const [accDetails, setAccDetails] = useState(null);
    const [envDetails, setEnvDetails] = useState(null);

    function clearAccountDetails() {
        setAccSummary(null);
        setAccDetails(null);
    }

    function clearEnvironmentDetails() {
        setEnvSummary(null);
        setEnvDetails(null);
    }

    const context = useMemo(() => {
        function handleAccountUpdated({ uid }) {
            const { onCreate } = accDetails;
            if (isFunction(onCreate)) {
                onCreate(uid);
            }
            clearAccountDetails();
        }

        function handleEnvironmentUpdated({ uid }) {
            const { onCreate } = envDetails;
            if (isFunction(onCreate)) {
                onCreate(uid);
            }
            clearEnvironmentDetails();
        }

        return {
            onEditAccount: setAccDetails,
            onEditEnvironment: setEnvDetails,
            onUpdateAccount: handleAccountUpdated,
            onUpdateEnvironment: handleEnvironmentUpdated,
            onUpdateAccountSummary: setAccSummary,
            onUpdateEnvironmentSummary: setEnvSummary,
            onUpdateProjectSummary: setProjectSummary
        };
    }, [accDetails, envDetails]);

    return (
        <DialogWithChapters
            title={t('components:CreateProjectEnv.newProject')}
            onClose={onClose}
            id="new-project-dialog">
            <DialogContext.Provider value={context}>
                <DialogFormContent>
                    {accDetails && (
                        <ConfigureServiceAccountPanel
                            id={accId}
                            account={accDetails.account}
                            onCancel={clearAccountDetails}
                        />
                    )}
                    {envDetails && (
                        <ConfigureEnvironmentPanel
                            id={envId}
                            environment={envDetails.environment}
                            onCancel={clearEnvironmentDetails}
                            open={true}
                        />
                    )}
                    <DialogConfigurePanel
                        id={projectId}
                        open={!envDetails}
                        title={t('components:CreateProjectEnv.configure')}>
                        <CreateProjectForm />
                    </DialogConfigurePanel>
                </DialogFormContent>
                <DialogSidebar>
                    {accSummary && <DialogSummary formId={accId} summary={accSummary} />}
                    {envSummary && <DialogSummary formId={envId} summary={envSummary} />}
                    <DialogSummary formId={projectId} summary={projectSummary} open={!envSummary} />
                </DialogSidebar>
            </DialogContext.Provider>
        </DialogWithChapters>
    );
}
